import React, { useState } from 'react';
import { Box, Paper, Typography, Button, Tabs, Tab, TextField, LinearProgress, Alert, Chip } from '@mui/material';
import { CloudUpload, ContentPaste, CheckCircle, Link } from '@mui/icons-material';
import { proposalService, tokenManager } from '../services/api';

const SP_SITE_URL = process.env.REACT_APP_SP_SITE_URL || '';
const SP_FOLDER = process.env.REACT_APP_SP_FOLDER || 'ProposalGenerator';

export default function StepUpload({ session, setSession, onNext }) {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [pastedText, setPastedText] = useState('');
  const [files, setFiles] = useState([]);
  const [spLink, setSpLink] = useState('');

  const handleUpload = async () => {
    if (files.length === 0) return;
    setLoading(true);
    setStatus({ type: 'info', msg: `Uploading ${files.length} file(s) and extracting text...` });
    try {
      const result = await proposalService.upload(files);
      const updated = { ...session, sessionId: result.session_id, fileName: result.file_name };

      if (tokenManager.isAuthenticated() && SP_SITE_URL) {
        setStatus({ type: 'info', msg: 'Storing to SharePoint...' });
        try {
          const spResult = await proposalService.uploadToSharePoint(result.session_id, SP_SITE_URL, SP_FOLDER);
          updated.spUrl = spResult.web_url;
          setStatus({ type: 'success', msg: `✅ Stored to SharePoint: ${spResult.file_name}` });
        } catch (err) {
          setStatus({ type: 'warning', msg: `SharePoint upload skipped: ${err.response?.data?.detail || err.message}` });
        }
      } else {
        setStatus({ type: 'success', msg: `Extracted ${result.text_length} characters from ${result.file_count} file(s)` });
      }

      setSession(updated);
      setTimeout(onNext, 1200);
    } catch (err) {
      setStatus({ type: 'error', msg: `Error: ${err.response?.data?.detail || err.message}` });
    } finally { setLoading(false); }
  };

  const handleSharePointFetch = async () => {
    if (!spLink.trim()) return;
    if (!tokenManager.isAuthenticated()) {
      setStatus({ type: 'error', msg: 'Please sign in first to access SharePoint documents.' });
      return;
    }
    const urls = spLink.trim().split('\n').filter(l => l.trim());
    setLoading(true);
    setStatus({ type: 'info', msg: `Fetching ${urls.length} document(s) from SharePoint...` });
    try {
      const result = await proposalService.fetchFromSharePoint(urls);
      setSession({ ...session, sessionId: result.session_id, fileName: result.file_name, spUrl: urls[0] });
      setStatus({ type: 'success', msg: `✅ Fetched: ${result.file_name} (${result.text_length} chars extracted)` });
      setTimeout(onNext, 1000);
    } catch (err) {
      setStatus({ type: 'error', msg: `SharePoint error: ${err.response?.data?.detail || err.message}` });
    } finally { setLoading(false); }
  };

  const handlePaste = async () => {
    if (!pastedText.trim()) return;
    setLoading(true);
    try {
      const blob = new Blob([pastedText], { type: 'text/plain' });
      const f = new File([blob], 'pasted_rfp.txt', { type: 'text/plain' });
      const result = await proposalService.upload([f]);
      setSession({ ...session, sessionId: result.session_id, fileName: 'pasted_rfp.txt' });
      onNext();
    } catch (err) { setStatus({ type: 'error', msg: err.message }); }
    finally { setLoading(false); }
  };

  return (
    <Paper sx={{ p: 4, border: '1px solid #E2E8F0' }}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab icon={<CloudUpload />} label="Upload Files" iconPosition="start" />
        <Tab icon={<Link />} label="SharePoint URL" iconPosition="start" />
        <Tab icon={<ContentPaste />} label="Paste Text" iconPosition="start" />
      </Tabs>

      {/* Tab 0: Upload Files */}
      {tab === 0 && (
        <Box>
          <Box sx={{ border: '2px dashed', borderColor: files.length > 0 ? '#6D28D9' : '#CBD5E1', borderRadius: 3, p: 8, textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { borderColor: '#6D28D9', bgcolor: '#FAF5FF' } }}
            onClick={() => document.getElementById('file-input').click()}>
            <input id="file-input" type="file" accept=".pdf,.docx,.txt,.xlsx,.xls,.pptx,.ppt,.csv" multiple hidden onChange={e => setFiles(Array.from(e.target.files))} />
            {files.length > 0 ? (
              <Box>
                <CheckCircle sx={{ fontSize: 56, color: '#16A34A', mb: 1 }} />
                <Typography variant="h6">{files.length} file(s) selected</Typography>
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 0.5 }}>
                  {files.map((f, i) => (
                    <Chip key={i} label={`${f.name} (${(f.size / 1024).toFixed(0)} KB)`} size="small" />
                  ))}
                </Box>
              </Box>
            ) : (
              <Box>
                <CloudUpload sx={{ fontSize: 56, color: '#CBD5E1', mb: 1 }} />
                <Typography color="#64748B">Click to select files (multiple allowed)</Typography>
                <Typography variant="caption" color="#94A3B8" sx={{ mt: 1, display: 'block' }}>
                  Supported: PDF, DOCX, XLSX, PPTX, TXT, CSV
                </Typography>
              </Box>
            )}
          </Box>
          {tokenManager.isAuthenticated() && SP_SITE_URL && (
            <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>📎 Files will be auto-uploaded to SharePoint</Alert>
          )}
          <Button variant="contained" fullWidth size="large" onClick={handleUpload} disabled={files.length === 0 || loading}
            sx={{ mt: 2, py: 1.5, bgcolor: '#6D28D9', '&:hover': { bgcolor: '#5B21B6' } }}>
            {loading ? 'Processing...' : `Upload ${files.length > 0 ? files.length + ' file(s)' : ''} & Proceed →`}
          </Button>
        </Box>
      )}

      {/* Tab 1: SharePoint URLs */}
      {tab === 1 && (
        <Box>
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
            ⚠️ <strong>Note:</strong> Due to organizational security policies, direct file download from SharePoint is restricted on this device.
            Please use the <strong>Upload Files</strong> tab instead — download the files from SharePoint in your browser first, then upload them here.
          </Alert>
          <TextField fullWidth multiline rows={5} placeholder="Paste SharePoint URLs (one per line):\nhttps://wipro365.sharepoint.com/sites/.../file1.docx\nhttps://wipro365.sharepoint.com/sites/.../file2.xlsx" value={spLink} onChange={e => setSpLink(e.target.value)}
            label="SharePoint Document/Folder URLs"
            helperText="Paste file URLs or folder URLs (one per line). Folders will auto-fetch all supported files inside."
            sx={{ mb: 2 }} />
          {!tokenManager.isAuthenticated() && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>Sign in first to access SharePoint documents.</Alert>
          )}
          <Button variant="contained" fullWidth size="large" onClick={handleSharePointFetch}
            disabled={!spLink.trim() || loading || !tokenManager.isAuthenticated()}
            sx={{ py: 1.5, bgcolor: '#6D28D9', '&:hover': { bgcolor: '#5B21B6' } }}>
            {loading ? 'Fetching from SharePoint...' : `📎 Fetch ${spLink.trim().split('\n').filter(l => l.trim()).length || ''} URL(s) & Proceed →`}
          </Button>
        </Box>
      )}

      {/* Tab 2: Paste Text */}
      {tab === 2 && (
        <Box>
          <TextField multiline rows={12} fullWidth placeholder="Paste your RFP text here..."
            value={pastedText} onChange={e => setPastedText(e.target.value)} />
          <Button variant="contained" fullWidth size="large" onClick={handlePaste} disabled={!pastedText.trim() || loading}
            sx={{ mt: 3, py: 1.5, bgcolor: '#6D28D9', '&:hover': { bgcolor: '#5B21B6' } }}>
            {loading ? 'Processing...' : 'Submit & Proceed →'}
          </Button>
        </Box>
      )}

      {loading && <LinearProgress sx={{ mt: 2 }} />}
      {status && <Alert severity={status.type} sx={{ mt: 2, borderRadius: 2 }}>{status.msg}</Alert>}
    </Paper>
  );
}
